import { useState } from 'react';
import PropTypes from 'prop-types';
import { shortName } from '../calendar/mergeGroupEvents.js';

/**
 * Invited groups, as a tree rather than a flat run of chips.
 *
 * Inviting a 97-member group used to fill the dialog with 97 chips: no way to
 * see the appointment being created, and no practical way to drop one person.
 * A group shows as one line with its count, folds open to its members, and
 * each member can be unticked.
 *
 * `excluded` is a Set of uuids held by the parent — the dialog needs it to
 * know who it is actually inviting.
 */
export default function InviteeGroups({ groups, excluded, onToggleMember, onRemoveGroup }) {
  if (!groups || groups.length === 0) return null;

  return (
    <ul className="mb-2 divide-y divide-gray-100 rounded-md border border-gray-200">
      {groups.map((group) => (
        <GroupLine
          key={group.id}
          group={group}
          excluded={excluded}
          onToggleMember={onToggleMember}
          onRemoveGroup={() => onRemoveGroup(group.id)}
        />
      ))}
    </ul>
  );
}

InviteeGroups.propTypes = {
  groups: PropTypes.array,
  excluded: PropTypes.instanceOf(Set).isRequired,
  onToggleMember: PropTypes.func.isRequired,
  onRemoveGroup: PropTypes.func.isRequired,
};

function GroupLine({ group, excluded, onToggleMember, onRemoveGroup }) {
  const [open, setOpen] = useState(false);
  const kept = group.members.filter((m) => !excluded.has(m.uuid)).length;
  const total = group.members.length;

  return (
    <li>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <button
          type="button"
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          <svg
            className={`h-3 w-3 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="truncate text-sm font-medium text-gray-800" title={group.name}>
            {group.name}
          </span>
          <span className="shrink-0 rounded-full bg-brand-100 px-2 text-xs text-brand-900">
            {kept === total ? total : `${kept}/${total}`}
          </span>
        </button>
        <button
          type="button"
          onClick={onRemoveGroup}
          title={`Retirer ${group.name}`}
          aria-label={`Retirer ${group.name}`}
          className="shrink-0 rounded px-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
        >
          &times;
        </button>
      </div>

      {open && (
        <ul className="max-h-48 overflow-y-auto border-t border-gray-100 bg-gray-50 px-2 py-1">
          {group.members.map((member) => (
            <li key={member.uuid}>
              <label className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-white">
                <input
                  type="checkbox"
                  checked={!excluded.has(member.uuid)}
                  onChange={() => onToggleMember(member.uuid)}
                  className="h-3 w-3 shrink-0 accent-[var(--brand-primary)]"
                />
                <span className="truncate text-xs text-gray-600" title={member.name}>
                  {shortName(member.name)}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

GroupLine.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    members: PropTypes.array,
  }).isRequired,
  excluded: PropTypes.instanceOf(Set).isRequired,
  onToggleMember: PropTypes.func.isRequired,
  onRemoveGroup: PropTypes.func.isRequired,
};
